/**
 * When a half-time pre-order turns ready at its collection point, the fan
 * page brings the fan to its detail screen once. Two rules keep that from
 * hijacking the wrong moment:
 *
 * - "Once" must survive a reload. The mark used to live in a React ref, so
 *   every reload re-opened it — and a MobilePay / iDEAL payment always
 *   reloads the page on return.
 * - Never over a now-order the fan is following. HJK vs Gnistan, 17 Oct
 *   2026: a fan with a ready pre-order paid for a seat delivery, and the
 *   pre-order's "ready for pick-up" screen replaced the delivery's
 *   confirmation; they read it as their delivery being ready for pick-up.
 *   The green strip on every screen already says the pre-order is ready.
 */
export function shouldAutoOpenReadyPreorder(args: {
  preorderId: string | null;
  preorderStatus: string | null;
  /** The pre-order already auto-opened on this device, if any. */
  shownPreorderId: string | null;
  /** Cart or payment sheet open: replacing it would lose the fan's input. */
  checkoutInProgress: boolean;
  /** A now-order placed by this fan is still pending / being prepared. */
  nowOrderInProgress: boolean;
}): boolean {
  const { preorderId, preorderStatus, shownPreorderId, checkoutInProgress, nowOrderInProgress } = args;
  if (!preorderId || preorderStatus !== 'ready_at_collection_point') return false;
  if (shownPreorderId === preorderId) return false;
  if (checkoutInProgress || nowOrderInProgress) return false;
  return true;
}

const shownKey = (tagId: string) => `idseat_preorder_ready_shown_${tagId}`;

export function readReadyPreorderShown(tagId: string): string | null {
  try {
    return localStorage.getItem(shownKey(tagId));
  } catch {
    return null;
  }
}

export function markReadyPreorderShown(tagId: string, preorderId: string): void {
  try {
    localStorage.setItem(shownKey(tagId), preorderId);
  } catch {
    // Storage unavailable (private mode, quota): it may open once more after a reload.
  }
}
